import Card from 'components/Card';
import Typography from 'components/Typography';
import {Integration, IntegrationOption} from 'types/integration';
import styles from '../IntegrationList/IntegrationList.module.scss';
import CardContent from 'components/CardContent';
import ButtonWithIcon from 'components/ButtonWithIcon';
import classNames from 'classnames';
import {CheckIcon} from 'pages/Signup/icons';
import PlusIcon from 'pages/RouteHistory/icons/plus';
import SyncIcon from '../../icons/sync';
import api from 'api';
import {useMutation, useQueryClient} from 'react-query';
import toast from 'react-hot-toast';
import {get} from 'lodash';
import ReactSwitch from 'react-switch';
import {useState} from 'react';
import CopyIcon from '../../icons/copy';
import TickIcon from '../../icons/tick';

type Props = {
  integration: IntegrationOption;
  integratedItem?: Integration;
  isIntegrated: boolean;
  isSyncing?: boolean;
  onConnect?: () => void;
  onDisconnect?: () => void;
  manualSync?: () => void;
};

const autoSync = async ({
  is_active,
  id,
}: {
  is_active?: boolean;
  id?: string;
}) => {
  if (!is_active && !id) {
    return;
  }
  return await api.put(`/integrations/integration-configs/${id}/`, {
    is_active,
  });
};
function IntegrationCard({
  integration,
  integratedItem,
  isIntegrated,
  isSyncing,
  onConnect,
  onDisconnect,
  manualSync,
}: Props) {
  const queryClient = useQueryClient();
  const [isActive, setIsActive] = useState(integratedItem?.is_active || false);
  const [isCopied, setIsCopied] = useState(false);
  const {mutateAsync: autoSyncMutation} = useMutation({
    mutationFn: autoSync,
    onSuccess: () => {
      const message = integratedItem?.is_active
        ? 'Auto sync is off'
        : 'Auto sync is on';
      toast.success(message);
      queryClient?.invalidateQueries('integrations');
    },
    onError: (err) => {
      const message = get(err, 'response.data.message', 'Something went wrong');
      toast.error(message);
    },
  });
  const handleToggleActive = () => {
    setIsActive((prev) => !prev);

    autoSyncMutation({
      id: integratedItem?.id,
      is_active: !integratedItem?.is_active,
    });
  };
  const handleCopyWebhookUrl = () => {
    if (isCopied) return;
    try {
      const webhookUrl = integratedItem?.webhook_url ?? '';
      if (!webhookUrl) {
        toast.error('No webhook URL found');
        return;
      }
      navigator.clipboard.writeText(webhookUrl);
      toast.success('Webhook URL copied to clipboard');
      setIsCopied(true);
    } catch (error) {
      console.error(error);
    } finally {
      setTimeout(() => {
        setIsCopied(false);
      }, 2000);
    }
  };
  const {logo, company_name, description} = integration;
  const [isBadgeHovered, setIsBadgeHovered] = useState(false);
  console.log(integratedItem);
  return (
    <Card
      className={classNames(
        styles.integrationCard,
        isIntegrated && styles.integratedCard
      )}
    >
      <CardContent className={styles.cardContent}>
        <div className={styles.header}>
          <div className={styles.headerLeft}>
            <div className={styles.logo}>{logo}</div>
            <div className={styles.titleContainer}>
              <Typography variant="h3" className={styles.companyName}>
                {company_name}
              </Typography>
              {isIntegrated && (
                <div className={styles.statusBadge}>
                  <div
                    className={styles.interactiveBadge}
                    onMouseEnter={() => setIsBadgeHovered(true)}
                    onMouseLeave={() => setIsBadgeHovered(false)}
                    onClick={onDisconnect}
                  >
                    {isBadgeHovered ? 'Disconnect' : 'Connected'}
                  </div>
                </div>
              )}
            </div>
          </div>
          {isIntegrated && (
            <div className={styles.headerRight}>
              <div className={styles.toggleContainer}>
                <ReactSwitch
                  onColor="#016630"
                  onChange={handleToggleActive}
                  handleDiameter={17}
                  height={20}
                  width={40}
                  className={styles.switch}
                  checked={isActive}
                />
              </div>
            </div>
          )}
        </div>

        <div className={styles.content}>
          <Typography variant="label" className={styles.description}>
            {description}
          </Typography>

          {integration?.entities && integration.entities?.length > 0 && (
            <div className={styles.entitiesContainer}>
              {integration.entities?.map((entity) => (
                <div
                  key={entity}
                  className={classNames(
                    integratedItem?.is_active
                      ? styles.syncedEntity
                      : styles.inactiveEntity
                  )}
                >
                  <CheckIcon width={14} height={14} />
                  <Typography variant="p" className={styles.entityText}>
                    {entity}
                  </Typography>
                </div>
              ))}
            </div>
          )}
        </div>

        <div className={styles.actions}>
          {isIntegrated ? (
            <>
              <ButtonWithIcon
                color="green-outlined"
                size="md"
                iconPosition="left"
                className={styles.actionButton}
                icon={
                  isCopied ? (
                    <TickIcon width={16} height={16} />
                  ) : (
                    <CopyIcon width={16} height={16} />
                  )
                }
                onClick={handleCopyWebhookUrl}
              >
                {isCopied ? 'Copied' : 'Copy Webhook'}
              </ButtonWithIcon>
              <ButtonWithIcon
                color="green"
                size="md"
                className={styles.actionButton}
                disabled={isSyncing}
                icon={
                  <SyncIcon
                    width={16}
                    height={16}
                    className={classNames(isSyncing && styles.animate)}
                  />
                }
                onClick={manualSync}
              >
                {isSyncing ? 'Syncing...' : 'Manual Sync'}
              </ButtonWithIcon>
            </>
          ) : (
            <ButtonWithIcon
              color={'green-outlined'}
              size="md"
              icon={<PlusIcon width={16} height={16} />}
              onClick={onConnect}
            >
              Connect
            </ButtonWithIcon>
          )}
        </div>
      </CardContent>
    </Card>
  );
}
export default IntegrationCard;
